import axios from 'axios';
import { api_labelflow } from "../config.jsx";

/**
 * The HTTP surface of the credential cascade (`/auth_management/*`).
 *
 * This is the ONLY module in the frontend that talks to the auth API. The login
 * hook, the connect modal and the export list all go through it, so there is one
 * place that knows the route names, one place that knows the body field spelling
 * and one place that decides what a failure means.
 *
 * Three things about it are deliberate and easy to "tidy" back into bugs:
 *
 * 1. EVERY CALL USES `validateStatus: () => true`, so axios NEVER rejects on a
 *    4xx. The backend answers a real envelope with a real reason on 401, 403,
 *    409 and 429 — and the whole point of the cascade is that those reasons are
 *    different from each other ("wrong password" vs "that address already has a
 *    LabelFlow account, type its password to link them" vs "this is a platform
 *    administrator account"). An axios throw collapses all of them into one
 *    catch block, which is exactly how the old login ended up reporting every
 *    outcome as "Wrong email or password".
 *
 * 2. EVERY CALL RETURNS THE SAME NORMALISED ENVELOPE
 *    `{ok, status, message, data, detail}` and never throws. Callers dispatch on
 *    `message`, never on `status`, and `ok` is strictly `message === "MSG_100"`.
 *    A 200 whose body is not the expected envelope is NOT ok — see `normalise`.
 *
 * 3. NO SHARED AXIOS INSTANCE. That is this repo's convention (every
 *    `pages/<page>/api/*.js` makes its own bare axios calls, and there is no
 *    interceptor anywhere); an instance here would be the first one and would
 *    read as the place to hang an auth interceptor, which this API has no
 *    concept of — endpoints prove the actor with a password in the body.
 *
 * No route is declared at the bare router prefix: FastAPI 307-redirects a
 * trailing-slash mismatch and builds `Location` from the Host header, which
 * behind this deployment's nginx is cross-origin AND mixed content, so the
 * browser refuses to follow it and the call silently returns nothing. See
 * 3_infra/nginx/README.md.
 */

/** The message codes the API answers with, plus the one the client invents. */
export const MSG = {
  OK: "MSG_100",
  UNAUTHORISED: "MSG_401",
  LINK_REQUIRED: "MSG_LINK_REQUIRED",
  REFUSED: "MSG_REFUSED",
  CLAIMED: "MSG_109",
  THROTTLED: "MSG_429",
  BAD_REQUEST: "MSG_400",
  // Never sent by the server: the request did not arrive or the reply did not
  // come back. A caller that treats this as MSG_401 tells somebody their
  // password is wrong when the truth is that the API is down.
  NETWORK: "MSG_NETWORK",
};

const BASE = `${api_labelflow}/auth_management`;

// A backstop only. The server bounds itself (LOGIN_PRIMARY_TIMEOUT_S per primary
// attempt plus PROBE_PHASE_BUDGET_S for the whole probe phase), so a login can
// legitimately take ten seconds or more and a tight client timeout would abandon
// a cascade that was about to succeed — costing the user a retyped password and
// a throttle unit. This exists so a hung socket does not spin forever.
const REQUEST_TIMEOUT_MS = 30000;

// What an UN-ENVELOPED answer means, by status. The API always answers the
// envelope; this map is for the deploy window (a new bundle against an API that
// has not been rebuilt yet) and for anything that answers on the API's behalf —
// an nginx error page, a gateway timeout.
//
// 200 maps to MSG_400 ON PURPOSE. The old `/login` answered a raw user document
// with no `message`, and reading that as success would hand `writeSession` a
// payload with no `user`, no `primaryProvider` and no targets — a half-made
// session that looks signed in. An unrecognised shape is a failed call.
//
// 409 maps to MSG_109 rather than MSG_LINK_REQUIRED for the same reason: without
// an envelope the two are indistinguishable, and the conservative one does not
// prompt for a second password.
//
// 403 IS DELIBERATELY ABSENT. MSG_REFUSED does not mean "forbidden" — it means a
// provider looked at this identity and refused it FOR A STATED REASON ("this is a
// platform administrator account; it owns no site"), and the whole payload of
// that answer is the sentence in `detail`. An un-enveloped 403 is nginx, CORS or
// a WAF: there is no verdict and no sentence. Manufacturing MSG_REFUSED from the
// status alone tells somebody their credentials were rejected when the truth is
// that the request never reached the cascade — a terminal, unexplained dead end
// instead of "try again later" — and invents client-side the one answer the
// design already accepts as a credential oracle. An infrastructure failure falls
// through to MSG_400, which is what it is.
const STATUS_FALLBACK = {
  400: MSG.BAD_REQUEST,
  401: MSG.UNAUTHORISED,
  409: MSG.CLAIMED,
  422: MSG.BAD_REQUEST,
  429: MSG.THROTTLED,
};

/**
 * THE DEPLOY WINDOW, AND THE ONE ANSWER THAT HAS TO BE ADOPTED.
 *
 * This frontend is served from a CRA dev server on this box and hot-reloads from
 * source; the management API is a DIFFERENT HOST (`interone.ai`, see
 * `3_infra/nginx/labelflow.conf`). So the browser can be running the cascade
 * while the API answering it is not, and on 2026-09-12 it was: `/providers`,
 * `/login/complete_link`, `/password/bootstrap` and `/services/*` all answered
 * 404 there, and `/login` answered FastAPI's raw 422 — i.e. the pre-cascade
 * build, whose `/login` returns **200 with the bare user document** and no
 * `message` key at all.
 *
 * `normalise` reads that as MSG_400 (deliberately — see STATUS_FALLBACK), so
 * every correct password was reported as "Failed to log in". A 200 that the user
 * is told is an error, which is exactly how it was reported.
 *
 * That check in `normalise` STAYS. A generic normaliser must not guess at an
 * unrecognised shape — the failure it prevents is a half-made session with no
 * `user` behind it. This adopter is the opposite thing: it runs only for
 * `/login`, it recognises ONE specific documented shape, and it builds the whole
 * envelope from it rather than letting an unknown one through.
 *
 * What it claims is true against that API, not a guess: it has no providers and
 * no linked services, so `targets: []` is the fact, not an absence of one.
 *
 * DELETE THIS THE DAY `interone.ai` IS REDEPLOYED. Same rule as g_portal's
 * `withLegacyFallback`: it exists for the window in which the two halves are at
 * different versions, and it is the last thing capable of signing somebody in
 * without the cascade having run.
 */

// Mirrors ALLOWED_USER_FIELDS in b_auth_management.py, minus the two fields a
// pre-cascade document cannot carry. It is an ALLOWLIST because the old answer
// also carries `cPassword` IN PLAINTEXT and a surveillance-shaped `cameraInfo`:
// that is the old API's leak either way, but nothing downstream of here needs to
// hold either, so nothing downstream of here is given them.
const LEGACY_USER_FIELDS = ["_id", "cName", "cEmail", "cCountry", "createdAt", "updatedAt"];

// The home provider's own identity. `ConnectedServices` lists
// `canExport && !isHome`, so this descriptor is filtered out of every row there
// and exists only so the sign-in summary can say "LabelFlow" rather than print
// the raw key.
const LEGACY_HOME_DESCRIPTOR = {
  key: "labelflow",
  label: "LabelFlow",
  isHome: true,
  canLogin: true,
  canExport: false,
  configured: true,
};

const adoptLegacyLogin = (status, body) => {
  if (status !== 200 || !body) return null;

  // The two fields that make this the old `/login` answer and not something
  // else. `_id` is stringified by that endpoint before it returns, so the same
  // "a string or a number, never a raw {$oid}" rule `writeSession` enforces
  // applies here — refusing is how a session with an unusable id never gets made.
  const id = body._id;
  if (typeof id !== "string" && typeof id !== "number") return null;
  if (!String(id) || typeof body.cEmail !== "string") return null;

  const user = {};
  LEGACY_USER_FIELDS.forEach((key) => {
    if (key in body) user[key] = body[key];
  });

  return {
    ok: true,
    status,
    message: MSG.OK,
    data: {
      user,
      primaryProvider: LEGACY_HOME_DESCRIPTOR.key,
      // No `mustSetPassword`: that API cannot provision a workspace from a
      // service sign-in, and claiming it would send the user to a bootstrap step
      // whose endpoint answers 404 there.
      targets: [],
      providers: [LEGACY_HOME_DESCRIPTOR],
    },
    detail: null,
  };
};

const networkEnvelope = (detail) => ({
  ok: false,
  status: 0,
  message: MSG.NETWORK,
  data: null,
  detail: detail || "Could not reach the server.",
});

/**
 * Turn an axios response into the normalised envelope.
 *
 * `detail` is rendered verbatim by the UI, so it is only ever kept when the
 * server sent a string. FastAPI's own validation errors put a LIST in `detail`,
 * and a list rendered into JSX is "[object Object]" at best.
 */
const normalise = (response, adopt) => {
  const status = response?.status ?? 0;
  const body = response?.data;
  const enveloped = body && typeof body === "object" && !Array.isArray(body);
  const messaged = enveloped && typeof body.message === "string" && body.message;

  // An answer that carries a `message` is this API's, whatever it says, and is
  // never adopted. `adopt` is consulted ONLY for a shape that has none — which
  // is the pre-cascade API or an infrastructure page, and only a caller that
  // knows its own route can tell those apart.
  if (!messaged && adopt) {
    const adopted = adopt(status, enveloped ? body : null);
    if (adopted) return adopted;
  }

  const message = messaged ? body.message : STATUS_FALLBACK[status] || MSG.BAD_REQUEST;

  const detail =
    enveloped && typeof body.detail === "string" && body.detail ? body.detail : null;

  // `data` is an OBJECT or null. `typeof [] === "object"`, so an array has to be
  // excluded by hand — every caller reads it by field (`data.user`,
  // `data.targets`), and an array would sail through each of those as `undefined`
  // and be stored as an empty list rather than reported as the malformed answer
  // it is.
  const data =
    enveloped && body.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : null;

  return { ok: message === MSG.OK, status, message, data, detail };
};

/**
 * One POST, normalised. Every auth route is a POST with a JSON body: a
 * credential must never travel in a query string, where it is written to
 * uvicorn's access log and the edge nginx's.
 */
const post = async (path, body, adopt) => {
  try {
    const response = await axios.post(`${BASE}${path}`, body, {
      headers: { "Content-Type": "application/json" },
      timeout: REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    return normalise(response, adopt);
  } catch (error) {
    // With `validateStatus` above, reaching here means no usable reply at all:
    // DNS, a refused connection, CORS, or the timeout.
    console.error(`auth_management${path} failed:`, error?.message || error);
    return networkEnvelope();
  }
};

/**
 * The provider catalogue — the descriptors that let the UI render a service it
 * has never heard of (monogram, accent, badge, copy, accepted model tasks).
 * Adding a provider is a backend-only change precisely because this is fetched
 * rather than hardcoded here.
 */
export async function getProviders() {
  try {
    const response = await axios.get(`${BASE}/providers`, {
      timeout: REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    return normalise(response);
  } catch (error) {
    console.error("auth_management/providers failed:", error?.message || error);
    return networkEnvelope();
  }
}

/**
 * Sign in with ONE email and ONE password, against every provider that can
 * answer for them. There is no provider picker: the cascade decides, because the
 * person signing in should not have to know which company holds their account.
 *
 * Answers worth dispatching on: MSG_100 (with `data.mustSetPassword` when the
 * workspace was provisioned by a service and has no password of its own),
 * MSG_LINK_REQUIRED, MSG_REFUSED, MSG_401, MSG_429.
 */
export async function login({ email, password }) {
  // The third argument is the deploy-window adopter, and `/login` is the only
  // route that gets one: it is the only pre-cascade route that still exists on
  // the deployed API. Every other route here 404s there, which normalises to
  // MSG_400 and is reported as the failure it is.
  return post("/login", { email, password }, adoptLegacyLogin);
}

/**
 * The second half of MSG_LINK_REQUIRED: the service credentials proved a site,
 * and this proves the LabelFlow account at the same address, which is the only
 * thing that may attach one to the other. LabelFlow is self-signup with no email
 * verification, so a matching address on its own proves nothing.
 */
export async function completeLinkLogin({ email, password, labelflowPassword }) {
  return post("/login/complete_link", {
    email,
    password,
    labelflow_password: labelflowPassword,
  });
}

/**
 * Give a service-provisioned workspace a password of its own. Until this
 * succeeds there is no session to persist — the caller must not write one.
 */
export async function bootstrapHomePassword({ email, password, provider, newPassword }) {
  return post("/password/bootstrap", {
    email,
    password,
    provider,
    new_password: newPassword,
  });
}

/**
 * Link a service to the signed-in workspace. `actorPassword` is the caller's own
 * LabelFlow password: `user_id` is a Mongo id out of localStorage and proves
 * nothing, so every `/services/*` route demands the actor prove themselves
 * before it reads or writes anything.
 */
export async function connectService({ userId, provider, identifier, password, actorPassword }) {
  return post("/services/connect", {
    user_id: userId,
    provider,
    identifier,
    password,
    actor_password: actorPassword,
  });
}

/** Forget a link here. Nothing changes on the service itself. */
export async function disconnectService({ userId, provider, actorPassword }) {
  return post("/services/disconnect", {
    user_id: userId,
    provider,
    actor_password: actorPassword,
  });
}

/**
 * Re-read the stored targets. A POST, not a GET: a GET keyed on a guessable
 * user id would be an unauthenticated read of somebody's linked sites, and a GET
 * carrying the actor's password would put it in two access logs.
 */
export async function getTargets({ userId, actorPassword }) {
  return post("/services/list", { user_id: userId, actor_password: actorPassword });
}

/**
 * Probe every configured service live, ignoring what was remembered. This is the
 * way out of a cached "no account here" after the site has since been created —
 * without it a negative answer would stand for a day.
 */
export async function recheckServices({ userId, actorPassword }) {
  return post("/services/recheck", { user_id: userId, actor_password: actorPassword });
}
